import { useState, useEffect, ChangeEvent } from 'react';
import { Card, Col, Offcanvas, Row, Toast, ToastContainer } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';


interface Resource {
    resource_id: number;
    resource_name: string;
    resource_type: string;
    meta: string;
    resource_uri: string;
    notes: string;
    inserted_at: string;
}

const ResourcesTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};

interface ResourceFormProps {
    resource: Resource;
}

const ResourceForm: React.FC<ResourceFormProps> = ({ resource }) => {

    const auth = useAuth();
    const [loading, setLoading] = useState(false);

    const handleDownloadClick = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(
                `/v1/resources/${resource.resource_id}/download`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );


            const fileUrl = response.data['resource_url'];

            // Trigger download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-2">
            <form>
                <div className="mb-3">
                    <label htmlFor="resourceName" className="form-label">Resource Name</label>
                    <input type="text" className="form-control" id="resourceName" value={resource.resource_name} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="resourceType" className="form-label">Resource Type</label>
                    <input type="text" className="form-control" id="resourceType" value={resource.resource_type} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="meta" className="form-label">Meta</label>
                    <input type="text" className="form-control" id="meta" value={resource.meta} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="resourceUri" className="form-label">Resource URI</label>
                    <input type="text" className="form-control" id="resourceUri" value={resource.resource_uri} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="notes" className="form-label">Notes</label>
                    <textarea className="form-control" id="notes" rows={3} value={resource.notes} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="insertedAt" className="form-label">Inserted At</label>
                    <input type="text" className="form-control" id="insertedAt" value={resource.inserted_at} readOnly />
                </div>
                <div className="mb-3">

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDownloadClick}
                        disabled={loading}
                    >
                        {loading ? 'Downloading...' : 'Download File'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const Resources = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('Learning'),
            url: '/learning'
        },
        {
            label: t('Resources.page_title'),
            active: true
        }
    ];
    const [showResource, setShowResource] = useState(false);
    const handleClose = () => setShowResource(false);


    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    const [resources, setResources] = useState<Resource[]>([]);
    const [resource, setResource] = useState<Resource>();

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const certificateListTableColumns: ColumnDef<Resource>[] = [
        {
            header: t('Resource.table.title'),
            accessorKey: 'resource_name',
            cell: ({ row: { original } }) => {
                const { resource_id, resource_name } = original;

                // Example method to handle the click event
                const handleClick = () => {
                    setResource(original);
                    setShowResource(true);
                    // Call your desired method here
                };

                return (
                    <span
                        onClick={handleClick}
                        className="fw-semi-bold text-primary cursor-pointer"
                        style={{ textDecoration: 'underline' }} // Optional: to mimic link styling
                    >
                        {resource_name}
                    </span>
                );

            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },


        {
            header: t('Resource.table.type'),
            accessorKey: 'resource_type',
            meta: {
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: resources,
        columns: certificateListTableColumns,
        pageSize: 10,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/resources',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            setResources(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>

            <PageBreadcrumb items={breadcrumbItems} />

            <Offcanvas show={showResource} onHide={handleClose} backdrop="static" placement="end" style={{ 'width': '35%' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className='fs-5' >Resource details</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {resource && <ResourceForm resource={resource} />}
                </Offcanvas.Body>
            </Offcanvas>

            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Card className='mt-3  mb-3 border border-light rounded-5' >
                <Card.Body>
                    <AdvanceTableProvider {...table}>
                        <Row className="g-3 justify-content-between mb-4">
                            <Col xs="auto">
                                <Button
                                    variant="link"
                                    className="text-900 px-0"
                                    startIcon={
                                        <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                                    }
                                >
                                    {t("Resources.export")}
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <div className="d-flex">
                                    <SearchBox
                                        placeholder="Search by name"
                                        className="me-2"
                                        onChange={handleSearchInputChange}
                                    />
                                    <DatePicker defaultValue="Mar 1, 2022" />
                                    <Button
                                        variant="phoenix-secondary"
                                        className="px-3 ms-2"
                                        onClick={() => setOpenFilterModal(true)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFilter}
                                            transform="down-3"
                                            className="text-primary"
                                        />
                                    </Button>
                                </div>
                                <LeadsFilterModal
                                    show={openFilterModal}
                                    handleClose={() => setOpenFilterModal(false)}
                                />
                            </Col>
                        </Row>
                        <ResourcesTable />
                    </AdvanceTableProvider>
                </Card.Body>
            </Card>
        </div>
    );


};

export default Resources;
